import React, { useEffect, useState } from 'react'
import {
    Box,
    Typography,
    IconButton,
    Popover,
    TextField,
    Button,
    CircularProgress,
    Paper,
} from '@material-ui/core'
import { CheckCircle, Error as ErrorIcon, Edit } from '@material-ui/icons'

import { AuditValueStatus } from '../../types'
import { useAuditResultsDialogStyles } from './styles'

interface AuditResultsContentProps {
    attributeName: string
    rentRollFile?: string
    rentRollCoordinates: string | null
    leaseDocumentFile: string
    documentPageNumber: number | null
    rentRollValue: string | null
    documentValue: string | null
    conflictValue?: string | null
    status: AuditValueStatus
    reviewNote?: string | null
    reviewNoteAuthor?: string | null
    onSaveReviewNote?: (note: string | null) => Promise<void> | void
    isSavingReviewNote?: boolean
    onUpdateDocumentValue?: (value: string | null) => Promise<void> | void
    isUpdatingDocumentValue?: boolean
    showViewers?: boolean
    title?: string
    subtitle?: string
    headerLayout?: 'stacked' | 'inline'
}

export const AuditResultsContent: React.FC<AuditResultsContentProps> = ({
    attributeName,
    rentRollFile,
    rentRollCoordinates,
    leaseDocumentFile,
    documentPageNumber,
    rentRollValue,
    documentValue,
    conflictValue,
    status,
    reviewNote,
    reviewNoteAuthor,
    onSaveReviewNote,
    isSavingReviewNote = false,
    onUpdateDocumentValue,
    isUpdatingDocumentValue = false,
    showViewers = true,
    title,
    subtitle,
    headerLayout = 'stacked',
}) => {
    const classes = useAuditResultsDialogStyles()
    const [editAnchorEl, setEditAnchorEl] = useState<HTMLElement | null>(null)
    const [draftDocumentValue, setDraftDocumentValue] = useState<string>('')
    const [draftReviewNote, setDraftReviewNote] = useState<string>('')
    const [isEditingReviewNote, setIsEditingReviewNote] = useState(false)

    useEffect(() => {
        setDraftDocumentValue(documentValue ?? '')
    }, [documentValue])

    useEffect(() => {
        setDraftReviewNote(reviewNote ?? '')
        setIsEditingReviewNote(false)
    }, [title, reviewNote])

    const handleOpenEdit = (event: React.MouseEvent<HTMLElement>) => {
        setEditAnchorEl(event.currentTarget)
    }

    const handleCloseEdit = () => {
        setEditAnchorEl(null)
        setDraftDocumentValue(documentValue ?? '')
    }

    const handleSaveDocumentValue = async () => {
        if (!onUpdateDocumentValue) return

        const trimmedValue = draftDocumentValue.trim()
        const nextValue = trimmedValue.length > 0 ? trimmedValue : null

        try {
            await onUpdateDocumentValue(nextValue)
            setEditAnchorEl(null)
        } catch {
            // Keep popover open to allow retry or adjustment
        }
    }

    const handleSaveReviewNote = async () => {
        if (!onSaveReviewNote) return

        const trimmedNote = draftReviewNote.trim()
        const nextNote = trimmedNote.length > 0 ? trimmedNote : null
        if (nextNote === (reviewNote ?? null)) return

        try {
            await onSaveReviewNote(nextNote)
            setIsEditingReviewNote(false)
        } catch {
            setDraftReviewNote(reviewNote ?? '')
        }
    }

    const handleCancelReviewNote = () => {
        setDraftReviewNote(reviewNote ?? '')
        setIsEditingReviewNote(false)
    }

    const reviewNoteUnchanged =
        draftReviewNote.trim() === (reviewNote ?? '').trim()

    const rentRollUrl = rentRollFile
        ? rentRollCoordinates
            ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                  rentRollFile,
              )}&activeCell=${rentRollCoordinates}`
            : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                  rentRollFile,
              )}`
        : undefined

    const leaseDocumentUrl = leaseDocumentFile
        ? documentPageNumber
            ? `${leaseDocumentFile}#page=${documentPageNumber}`
            : leaseDocumentFile
        : undefined

    const documentValueDisplay = (
        <Box>
            <Box className={classes.valueRow}>
                <Typography className={classes.valueText}>
                    {documentValue || '-'}
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleOpenEdit}
                    disabled={!onUpdateDocumentValue || isUpdatingDocumentValue}
                    aria-label="Edit document value"
                >
                    <Edit fontSize="small" />
                </IconButton>
            </Box>
            {conflictValue ? (
                <Typography variant="caption" color="textSecondary">
                    {`Also found ${conflictValue} on another signed document.`}
                </Typography>
            ) : null}
        </Box>
    )

    const reviewNoteButton = onSaveReviewNote && !isEditingReviewNote && (
        <Button
            size="small"
            variant="contained"
            className={
                reviewNote
                    ? `${classes.darkButton} ${classes.reviewNoteButton}`
                    : classes.darkButton
            }
            onClick={() => setIsEditingReviewNote(true)}
            title={reviewNote || undefined}
        >
            <span className={classes.reviewNoteButtonLabel}>
                {reviewNote || 'Add Review Note'}
            </span>
        </Button>
    )

    const getStatusBadge = () => {
        switch (status) {
            case 'MATCHED':
                return {
                    icon: <CheckCircle style={{ fontSize: 16 }} />,
                    text: 'Matched',
                    className: classes.matchedBadge,
                }
            case 'NOT_MATCHED':
                return {
                    icon: <ErrorIcon style={{ fontSize: 16 }} />,
                    text: 'Not Matched',
                    className: classes.notMatchedBadge,
                }
            case 'MISSING_RENT_ROLL':
                return {
                    icon: <ErrorIcon style={{ fontSize: 16 }} />,
                    text: 'Missing Rent Roll',
                    className: classes.missingBadge,
                }
            case 'MISSING_DOCUMENT':
                return {
                    icon: <ErrorIcon style={{ fontSize: 16 }} />,
                    text: 'Missing Document',
                    className: classes.missingBadge,
                }
        }
    }

    const statusBadge = getStatusBadge()
    const isInlineHeader = headerLayout === 'inline'

    return (
        <>
            <Box className={classes.header}>
                <Box
                    className={`${classes.headerTop} ${
                        isInlineHeader ? classes.headerTopInline : ''
                    }`}
                >
                    <Box className={classes.headerTitle}>
                        <Box className={classes.headerTitleText}>
                            <Typography variant="h6">
                                {title ?? attributeName}
                            </Typography>
                            {subtitle && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                        <Box
                            className={`${classes.statusBadge} ${statusBadge.className}`}
                        >
                            {statusBadge.icon}
                            <span>{statusBadge.text}</span>
                        </Box>
                    </Box>
                    {isInlineHeader && (
                        <Box
                            className={`${classes.valuesContainer} ${
                                isInlineHeader ? classes.valuesInline : ''
                            }`}
                        >
                            <Box className={classes.valueBox}>
                                <Typography className={classes.valueLabel}>
                                    Rent Roll Value
                                </Typography>
                                <Typography className={classes.valueText}>
                                    {rentRollValue || '-'}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                vs
                            </Typography>
                            <Box className={classes.valueBox}>
                                <Typography className={classes.valueLabel}>
                                    Document Value
                                </Typography>
                                {documentValueDisplay}
                            </Box>
                            {reviewNoteButton}
                        </Box>
                    )}
                </Box>
                {!isInlineHeader && (
                    <Box className={classes.valuesContainer}>
                        <Box className={classes.valueBox}>
                            <Typography className={classes.valueLabel}>
                                Rent Roll Value
                            </Typography>
                            <Typography className={classes.valueText}>
                                {rentRollValue || '-'}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                            vs
                        </Typography>
                        <Box className={classes.valueBox}>
                            <Typography className={classes.valueLabel}>
                                Document Value
                            </Typography>
                            {documentValueDisplay}
                        </Box>
                        {reviewNoteButton}
                    </Box>
                )}
                {isEditingReviewNote && onSaveReviewNote && (
                    <Box className={classes.reviewNote}>
                        <TextField
                            label="Review Note"
                            value={draftReviewNote}
                            onChange={(event) =>
                                setDraftReviewNote(event.target.value)
                            }
                            variant="outlined"
                            size="small"
                            multiline
                            rows={2}
                            fullWidth
                            disabled={isSavingReviewNote}
                            placeholder="Both values are correct"
                            helperText={
                                reviewNoteAuthor
                                    ? `Saved by ${reviewNoteAuthor}. Appears as a comment on the Excel report.`
                                    : 'Appears as a comment on the Excel report, with your name.'
                            }
                        />
                        <Box className={classes.reviewNoteActions}>
                            <Button
                                onClick={handleCancelReviewNote}
                                size="small"
                                disabled={isSavingReviewNote}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSaveReviewNote}
                                variant="contained"
                                className={classes.darkButton}
                                size="small"
                                disabled={
                                    isSavingReviewNote || reviewNoteUnchanged
                                }
                            >
                                {isSavingReviewNote ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    'Save Note'
                                )}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
            <Popover
                open={Boolean(editAnchorEl)}
                anchorEl={editAnchorEl}
                onClose={handleCloseEdit}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box className={classes.popoverContent}>
                    <TextField
                        label="Document Value"
                        value={draftDocumentValue}
                        onChange={(event) =>
                            setDraftDocumentValue(event.target.value)
                        }
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault()
                                handleSaveDocumentValue()
                            }
                        }}
                        variant="outlined"
                        size="small"
                        autoFocus
                        disabled={isUpdatingDocumentValue}
                    />
                    <Box className={classes.popoverActions}>
                        <Button
                            onClick={handleCloseEdit}
                            size="small"
                            disabled={isUpdatingDocumentValue}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveDocumentValue}
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={isUpdatingDocumentValue}
                        >
                            {isUpdatingDocumentValue ? (
                                <CircularProgress size={16} />
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {showViewers && (
                <Box className={classes.content}>
                    {rentRollUrl && (
                        <Box className={classes.viewerContainer}>
                            <Typography className={classes.viewerTitle}>
                                Rent Roll
                                {rentRollCoordinates &&
                                    ` (Cell: ${rentRollCoordinates})`}
                            </Typography>
                            <Paper elevation={0}>
                                <iframe
                                    key={`rent-roll-${rentRollCoordinates}`}
                                    src={rentRollUrl}
                                    className={classes.viewerFrame}
                                    title="Rent Roll Viewer"
                                />
                            </Paper>
                        </Box>
                    )}

                    {leaseDocumentUrl && (
                        <Box className={classes.viewerContainer}>
                            <Typography className={classes.viewerTitle}>
                                Source Document
                                {documentPageNumber &&
                                    ` (Page: ${documentPageNumber})`}
                            </Typography>
                            <Paper elevation={0}>
                                <iframe
                                    key={`lease-doc-${leaseDocumentUrl}`}
                                    src={leaseDocumentUrl}
                                    className={classes.viewerFrame}
                                    title="Source Document Viewer"
                                />
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}
        </>
    )
}
